import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppRahmen } from '@/components/AppRahmen';
import { Hinweis, Knopf, Ladeflaeche } from '@/components/ui';
import { AuthAnbieter, useAuth } from '@/features/auth/AuthKontext';
import { AnmeldeSeite } from '@/features/auth/AnmeldeSeite';
import { Rollenschutz } from '@/features/auth/Rollenschutz';
import { Uebersicht } from '@/features/dashboard/Uebersicht';
import { amplifyKonfigurieren, istKonfiguriert } from '@/lib/amplify';
import { beobachteSystemModus, useDarstellung, wendeModusAn, type Farbmodus } from '@/lib/darkmode';
import { METAS, metaLesen, metaSchreiben } from '@/offline/db';
import { nutzerSchluesselMerken } from '@/offline/profil';
import { dauerhaftenSpeicherAnfordern } from '@/offline/speicher';
import { schliessWaechterStarten, synchronisieren, syncStarten } from '@/offline/sync';
import { PwaAktualisierung } from '@/components/PwaAktualisierung';

// Selten benutzte Bereiche werden erst bei Bedarf geladen — die App muss
// offline in unter 3 Sekunden starten (SPEC Kap. 13).
const ProtokollListe = lazy(() => import('@/features/protocols/ProtokollListe'));
const ProtokollAnlegen = lazy(() => import('@/features/protocols/ProtokollAnlegen'));
const ProtokollDetail = lazy(() => import('@/features/protocols/ProtokollDetail'));
const ProtokollErfassung = lazy(() => import('@/features/protocols/ProtokollErfassung'));
const ProtokollAbschluss = lazy(() => import('@/features/protocols/ProtokollAbschluss'));
const ObjektListe = lazy(() => import('@/features/objects/ObjektListe'));
const ObjektDetail = lazy(() => import('@/features/objects/ObjektDetail'));
const ObjektBearbeiten = lazy(() => import('@/features/objects/ObjektBearbeiten'));
const EinheitBearbeiten = lazy(() => import('@/features/objects/EinheitBearbeiten'));
const TerminListe = lazy(() => import('@/features/termine/TerminListe'));
const VorlagenListe = lazy(() => import('@/features/templates/VorlagenListe'));
const VorlagenEditor = lazy(() => import('@/features/templates/VorlagenEditor'));
const ImportSeite = lazy(() => import('@/features/import/ImportSeite'));
const VerwaltungSeite = lazy(() => import('@/features/admin/VerwaltungSeite'));
const ProfilSeite = lazy(() => import('@/features/profil/ProfilSeite'));
const ProtokollDownload = lazy(() => import('@/features/protocols/ProtokollDownload'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Daten kommen aus IndexedDB; das Netz ist nur die Nachschubquelle.
      staleTime: 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
      networkMode: 'offlineFirst',
    },
    mutations: { networkMode: 'offlineFirst' },
  },
});

const EinrichtungFehlt = () => (
  <main className="mx-auto max-w-2xl px-4 py-16">
    <Hinweis art="warnung" titel="Backend ist noch nicht verbunden">
      <p>
        Die Datei <code>amplify_outputs.json</code> fehlt. Sie entsteht beim ersten Deploy des
        Backends. Bis dahin kann sich niemand anmelden.
      </p>
      <p className="mt-2">
        Für eine lokale Umgebung: <code>npx ampx sandbox</code> ausführen. Für die produktive
        Umgebung siehe <code>docs/DEPLOYMENT.md</code>.
      </p>
    </Hinweis>
  </main>
);

const KeinZugriff = () => (
  <main className="mx-auto max-w-2xl px-4 py-16">
    <Hinweis art="warnung" titel="Kein Zugriff">
      Für diesen Bereich fehlt Ihrer Rolle die Berechtigung. Wenden Sie sich bitte an Ihre
      Administratorin oder Ihren Administrator.
    </Hinweis>
    <div className="mt-4">
      <Knopf aussehen="sekundaer" onClick={() => window.history.back()}>
        Zurück
      </Knopf>
    </div>
  </main>
);

/** Startet Sync und Hintergrunddienste, sobald jemand angemeldet ist. */
const AngemeldeteDienste = () => {
  const { schritt, nutzer } = useAuth();

  useEffect(() => {
    if (schritt !== 'angemeldet') return;

    void dauerhaftenSpeicherAnfordern();
    void metaSchreiben(METAS.angemeldetAls, nutzer?.benutzername ?? null);
    // Der Sync braucht den Profilschlüssel ohne Umweg über Cognito.
    void nutzerSchluesselMerken(nutzer?.sub ?? null);

    const syncStoppen = syncStarten();
    const waechterStoppen = schliessWaechterStarten();
    // Pull beim Login (SPEC Kap. 4).
    void synchronisieren(true);

    return () => {
      syncStoppen();
      waechterStoppen();
    };
  }, [schritt, nutzer?.benutzername, nutzer?.sub]);

  return null;
};

const Router = () => {
  const { schritt } = useAuth();

  if (schritt === 'laedt') return <Ladeflaeche text="Anmeldung wird geprüft …" />;

  return (
    <>
      <AngemeldeteDienste />
      <Suspense fallback={<Ladeflaeche />}>
        <Routes>
          <Route
            path="/anmelden"
            element={schritt === 'angemeldet' ? <Navigate to="/" replace /> : <AnmeldeSeite />}
          />
          <Route path="/kein-zugriff" element={<KeinZugriff />} />

          {/*
            Öffentlich und bewusst außerhalb von `Rollenschutz`: Hier landet,
            wer den Downloadlink aus der Protokollmail anklickt — ein Mieter
            oder Handwerksbetrieb ohne Zugang zum System. Ohne diese Route lief
            der Link in die Catch-all-Regel und damit auf die Anmeldung.
          */}
          <Route path="/protokoll/:token" element={<ProtokollDownload />} />

          <Route
            element={
              <Rollenschutz>
                <AppRahmen />
              </Rollenschutz>
            }
          >
            <Route index element={<Uebersicht />} />

            <Route path="protokolle" element={<ProtokollListe />} />
            <Route path="protokolle/neu" element={<ProtokollAnlegen />} />
            <Route path="protokolle/:id" element={<ProtokollDetail />} />
            <Route path="protokolle/:id/erfassen" element={<ProtokollErfassung />} />
            <Route path="protokolle/:id/abschluss" element={<ProtokollAbschluss />} />

            <Route path="objekte" element={<ObjektListe />} />
            <Route path="objekte/neu" element={<ObjektBearbeiten />} />
            <Route path="objekte/:id" element={<ObjektDetail />} />
            <Route path="objekte/:id/bearbeiten" element={<ObjektBearbeiten />} />
            <Route
              path="objekte/:objektId/einheiten/neu"
              element={
                <Rollenschutz rolle="Admin">
                  <EinheitBearbeiten />
                </Rollenschutz>
              }
            />
            <Route
              path="objekte/:objektId/einheiten/:einheitId/bearbeiten"
              element={
                <Rollenschutz rolle="Admin">
                  <EinheitBearbeiten />
                </Rollenschutz>
              }
            />

            <Route path="termine" element={<TerminListe />} />

            <Route path="profil" element={<ProfilSeite />} />

            <Route
              path="vorlagen"
              element={
                <Rollenschutz rolle="Admin">
                  <VorlagenListe />
                </Rollenschutz>
              }
            />
            <Route
              path="vorlagen/:id"
              element={
                <Rollenschutz rolle="Admin">
                  <VorlagenEditor />
                </Rollenschutz>
              }
            />
            <Route
              path="import"
              element={
                <Rollenschutz rolle="Admin">
                  <ImportSeite />
                </Rollenschutz>
              }
            />
            <Route
              path="verwaltung/*"
              element={
                <Rollenschutz rolle="Admin">
                  <VerwaltungSeite />
                </Rollenschutz>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
};

export const App = () => {
  const [bereit, setBereit] = useState(false);
  const setzeModus = useDarstellung((zustand) => zustand.setzeModus);

  useEffect(() => {
    const starten = async (): Promise<void> => {
      await amplifyKonfigurieren();
      const modus = await metaLesen<Farbmodus>(METAS.farbmodus, 'system');
      wendeModusAn(modus);
      setzeModus(modus);
      setBereit(true);
    };
    void starten();
    return beobachteSystemModus();
  }, [setzeModus]);

  // Farbwahl dauerhaft merken — in IndexedDB, nicht in localStorage.
  const modus = useDarstellung((zustand) => zustand.modus);
  useEffect(() => {
    if (bereit) void metaSchreiben(METAS.farbmodus, modus);
  }, [modus, bereit]);

  // Der Service Worker wird in jedem Zustand registriert — auch vor dem ersten
  // Deploy und vor der Anmeldung. Sonst könnte die App nach einem Neuladen
  // ohne Netz gar nicht erst starten, und das ist der Normalfall vor Ort.
  const rahmen = (inhalt: ReactNode): ReactNode => (
    <>
      {inhalt}
      <PwaAktualisierung />
    </>
  );

  if (!bereit) return rahmen(<Ladeflaeche text="Anwendung wird gestartet …" />);
  if (!istKonfiguriert()) return rahmen(<EinrichtungFehlt />);

  return rahmen(
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthAnbieter>
          <Router />
        </AuthAnbieter>
      </BrowserRouter>
    </QueryClientProvider>,
  );
};
